import type { QuizPersistenceIdentity } from './quizPersistenceIdentity';

const DEFAULT_USER_ID_KEY = 'p_user_id';
const DEFAULT_IS_ANONYMOUS_KEY = 'p_is_anonymous';

export type RpcIdentityParamKeyMap<
  UserIdKey extends string = typeof DEFAULT_USER_ID_KEY,
  IsAnonymousKey extends string = typeof DEFAULT_IS_ANONYMOUS_KEY,
> = {
  userId?: UserIdKey;
  isAnonymous?: IsAnonymousKey;
};

export type AuthenticatedRpcIdentityParams<
  IsAnonymousKey extends string = typeof DEFAULT_IS_ANONYMOUS_KEY,
> = {
  [Key in IsAnonymousKey]: false;
};

export type AnonymousRpcIdentityParams<
  UserIdKey extends string = typeof DEFAULT_USER_ID_KEY,
  IsAnonymousKey extends string = typeof DEFAULT_IS_ANONYMOUS_KEY,
> = {
  [Key in UserIdKey]: string;
} & {
  [Key in IsAnonymousKey]: true;
};

export type RpcIdentityParams<
  UserIdKey extends string = typeof DEFAULT_USER_ID_KEY,
  IsAnonymousKey extends string = typeof DEFAULT_IS_ANONYMOUS_KEY,
> =
  | AuthenticatedRpcIdentityParams<IsAnonymousKey>
  | AnonymousRpcIdentityParams<UserIdKey, IsAnonymousKey>;

/**
 * Shape used when the caller has not narrowed the identity's anonymous flag
 * yet. The key map is still reflected precisely, but the discriminant is
 * necessarily widened to boolean.
 */
export type DynamicRpcIdentityParams<
  UserIdKey extends string = typeof DEFAULT_USER_ID_KEY,
  IsAnonymousKey extends string = typeof DEFAULT_IS_ANONYMOUS_KEY,
> = {
  [Key in IsAnonymousKey]: boolean;
} & Partial<{
  [Key in UserIdKey]: string;
}>;

function resolveKey(value: string | undefined, fallback: string): string {
  const key = value ?? fallback;
  if (key.trim().length === 0) {
    throw new TypeError('RPC identity parameter keys must be non-empty');
  }
  return key;
}

/**
 * Formats the browser's canonical persistence identity for a self RPC.
 *
 * Authenticated calls deliberately omit the user-id field so the RPC must
 * resolve the owner from auth.uid(). Anonymous calls carry their explicit,
 * unregistered UUID because no JWT identity is available.
 */
export function toRpcIdentityParams<
  UserIdKey extends string = typeof DEFAULT_USER_ID_KEY,
  IsAnonymousKey extends string = typeof DEFAULT_IS_ANONYMOUS_KEY,
>(
  identity: QuizPersistenceIdentity & { isAnonymous: false },
  keyMap?: RpcIdentityParamKeyMap<UserIdKey, IsAnonymousKey>
): AuthenticatedRpcIdentityParams<IsAnonymousKey>;

export function toRpcIdentityParams<
  UserIdKey extends string = typeof DEFAULT_USER_ID_KEY,
  IsAnonymousKey extends string = typeof DEFAULT_IS_ANONYMOUS_KEY,
>(
  identity: QuizPersistenceIdentity & { isAnonymous: true },
  keyMap?: RpcIdentityParamKeyMap<UserIdKey, IsAnonymousKey>
): AnonymousRpcIdentityParams<UserIdKey, IsAnonymousKey>;

export function toRpcIdentityParams(identity: QuizPersistenceIdentity): DynamicRpcIdentityParams;

export function toRpcIdentityParams<UserIdKey extends string, IsAnonymousKey extends string>(
  identity: QuizPersistenceIdentity,
  keyMap: RpcIdentityParamKeyMap<UserIdKey, IsAnonymousKey>
): DynamicRpcIdentityParams<UserIdKey, IsAnonymousKey>;

export function toRpcIdentityParams<
  UserIdKey extends string = typeof DEFAULT_USER_ID_KEY,
  IsAnonymousKey extends string = typeof DEFAULT_IS_ANONYMOUS_KEY,
>(
  identity: QuizPersistenceIdentity,
  keyMap?: RpcIdentityParamKeyMap<UserIdKey, IsAnonymousKey>
): Record<string, unknown> {
  const userIdKey = resolveKey(keyMap?.userId, DEFAULT_USER_ID_KEY);
  const isAnonymousKey = resolveKey(keyMap?.isAnonymous, DEFAULT_IS_ANONYMOUS_KEY);

  if (userIdKey === isAnonymousKey) {
    throw new TypeError('RPC identity parameter keys must identify different fields');
  }

  if (identity.isAnonymous) {
    if (typeof identity.userId !== 'string' || identity.userId.trim() === '') {
      throw new TypeError('Anonymous RPC identity requires a userId');
    }

    return {
      [userIdKey]: identity.userId,
      [isAnonymousKey]: true,
    };
  }

  return {
    [isAnonymousKey]: false,
  };
}
